import { useEffect, useRef, useState, useCallback } from "react"
import Box from "@mui/material/Box"
import Stack from "@mui/material/Stack"
import Typography from "@mui/material/Typography"
import TextField from "@mui/material/TextField"
import MenuItem from "@mui/material/MenuItem"
import Button from "@mui/material/Button"
import IconButton from "@mui/material/IconButton"
import Switch from "@mui/material/Switch"
import CenteredModal from "@/src/common/centered-modal/CenteredModal"
import { Iconify } from "@/src/common/iconify"
import {
    CreditTypeConfigSection,
    type CreditTypeConfig,
    type CreditTypeConfigSectionProps,
} from "@/src/features/internalUsers/ui/CreditTypeConfigSection"
import { CHARGING_TYPE_OPTIONS, type ChargingTypeValue } from "@/src/features/internalUsers/constants"
import {
    CreateWorkspaceFormSchema,
    DEFAULT_CREATE_WORKSPACE_FORM_VALUES,
    type CreateWorkspaceFormValues,
} from "@/src/features/internalUsers/schemas/CreateWorkspace"
import { Divider } from "@mui/material"
import { createInternalWorkspace, type CreateWorkspaceCredits, type CreditsAddons } from "@/src/services/internal"
import type { ZodError } from "zod"

function zodFieldErrors(error: ZodError): Record<string, string> {
    const out: Record<string, string> = {}
    for (const e of error.errors) {
        const key = e.path.join(".")
        if (key) out[key] = e.message
    }
    return out
}

// ----------------------------------------------------------------------

export interface CreateWorkspaceModalProps {
    open: boolean
    onClose: () => void
    onSuccess?: () => void
}

function normalizeCreditConfig(c: CreditTypeConfig): CreateWorkspaceFormValues["videoConfig"] {
    return {
        ...c,
        credits: typeof c.credits === "string" ? parseInt(String(c.credits), 10) || 0 : c.credits,
        pulse: typeof c.pulse === "string" ? parseInt(String(c.pulse), 10) || 0 : c.pulse,
    }
}

const creditToggleRowSx = {
    p: 1.5,
    borderRadius: 1,
    bgcolor: "grey.50",
    border: 1,
    borderColor: "divider",
} as const

function CreditTypeToggleRow(props: {
    title: string
    description: string
    checked: boolean
    onCheckedChange: (checked: boolean) => void
}) {
    const { title, description, checked, onCheckedChange } = props
    return (
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={creditToggleRowSx}>
            <Box>
                <Typography variant="subtitle2">{title}</Typography>
                <Typography variant="caption" color="text.secondary">
                    {description}
                </Typography>
            </Box>
            <Switch checked={checked} onChange={(e) => onCheckedChange(e.target.checked)} color="primary" />
        </Stack>
    )
}

export function CreateWorkspaceModal({ open, onClose, onSuccess }: CreateWorkspaceModalProps) {
    const fileInputRef = useRef<HTMLInputElement>(null)
    const [form, setForm] = useState<CreateWorkspaceFormValues>(DEFAULT_CREATE_WORKSPACE_FORM_VALUES)
    const [outboundAudioEnabled, setOutboundAudioEnabled] = useState(false)
    const [logoFile, setLogoFile] = useState<File | null>(null)
    const [submitting, setSubmitting] = useState(false)
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

    const setFormField = useCallback(
        <K extends keyof CreateWorkspaceFormValues>(key: K, value: CreateWorkspaceFormValues[K]) => {
            setForm((prev) => ({ ...prev, [key]: value }))
            setFieldErrors((prev) => {
                const next = { ...prev }
                if (key === "videoConfig" || key === "audioConfig" || key === "outboundAudioConfig") {
                    const prefix =
                        key === "videoConfig"
                            ? "videoConfig."
                            : key === "audioConfig"
                              ? "audioConfig."
                              : "outboundAudioConfig."
                    Object.keys(next).forEach((k) => {
                        if (k.startsWith(prefix)) delete next[k]
                    })
                } else {
                    delete next[key as string]
                }
                return next
            })
        },
        []
    )

    const anyCreditTypeEnabled = form.videoEnabled || form.audioEnabled || outboundAudioEnabled
    const isCreateDisabled =
        !form.workspaceName.trim() || !form.websiteLink.trim() || !form.aboutCompany.trim() || !anyCreditTypeEnabled

    const handleClose = () => {
        setForm(DEFAULT_CREATE_WORKSPACE_FORM_VALUES)
        setOutboundAudioEnabled(false)
        setLogoFile(null)
        setFieldErrors({})
        onClose()
    }

    const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null)

    useEffect(() => {
        if (!logoFile) {
            setLogoPreviewUrl(null)
            return
        }
        const url = URL.createObjectURL(logoFile)
        setLogoPreviewUrl(url)
        return () => URL.revokeObjectURL(url)
    }, [logoFile])

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (file) setLogoFile(file)
    }

    const handleRemoveLogo = (e: React.MouseEvent) => {
        e.stopPropagation()
        setLogoFile(null)
        if (fileInputRef.current) fileInputRef.current.value = ""
    }

    const handleCreateWorkspace = async (e: React.FormEvent) => {
        e.preventDefault()
        const parsed = CreateWorkspaceFormSchema.safeParse(form)
        if (!parsed.success) {
            setFieldErrors(zodFieldErrors(parsed.error))
            return
        }
        setFieldErrors({})
        const values = parsed.data
        setSubmitting(true)
        try {
            await createInternalWorkspace(
                {
                    workspaceName: values.workspaceName,
                    websiteLink: values.websiteLink || undefined,
                    aboutCompany: values.aboutCompany || undefined,
                    audioCredits: {
                        isEnabled: values.audioEnabled,
                        totalCredits: values.audioEnabled ? values.audioConfig.credits : 0,
                        addons: {
                            pulse: values.audioEnabled ? values.audioConfig.pulse : 1,
                            proctoring: false,
                            cefr: values.audioEnabled ? values.audioConfig.cefr : false,
                        } as CreditsAddons,
                    },
                    videoCredits: {
                        isEnabled: values.videoEnabled,
                        totalCredits: values.videoEnabled ? values.videoConfig.credits : 0,
                        addons: values.videoEnabled
                            ? {
                                  pulse: values.videoEnabled ? values.videoConfig.pulse : 1,
                                  proctoring: values.videoEnabled ? values.videoConfig.proctoring : false,
                                  cefr: values.videoEnabled ? values.videoConfig.cefr : false,
                              }
                            : undefined,
                    } as CreateWorkspaceCredits,
                    outboundAudioCredits: {
                        isEnabled: outboundAudioEnabled,
                        totalCredits: outboundAudioEnabled ? values.outboundAudioConfig.credits : 0,
                        addons: {
                            pulse: outboundAudioEnabled ? values.outboundAudioConfig.pulse : 1,
                            proctoring: false,
                            cefr: outboundAudioEnabled ? values.outboundAudioConfig.cefr : false,
                        } as CreditsAddons,
                    },
                    chargeAttemptRule: values.chargingType,
                    maxAttempts: parseInt(values.attemptFrequency, 10) || 3,
                },
                logoFile
            )
            onSuccess?.()
            handleClose()
        } catch (error) {
            console.error("Error creating workspace:", error)
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <CenteredModal open={open} onClose={handleClose} width={800}>
            <Box
                component="form"
                onSubmit={handleCreateWorkspace}
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    minHeight: 0,
                }}
            >
                <Box
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        overflowY: "auto",
                        overflowX: "hidden",
                    }}
                >
                    {/* Header */}
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{
                            mx: -3,
                            mt: -3,
                            px: 3,
                            py: 2,
                            color: "grey.900",
                        }}
                    >
                        <Typography variant="h6" fontWeight={600}>
                            Create Workspace
                        </Typography>
                        <IconButton size="small" onClick={handleClose} sx={{ color: "inherit" }} aria-label="Close">
                            <Iconify icon="mingcute:close-line" width={24} />
                        </IconButton>
                    </Stack>

                    <Stack spacing={3} sx={{ mt: 2 }}>
                        {/* COMPANY INFO */}
                        <Box>
                            <Typography
                                variant="overline"
                                sx={{
                                    color: "text.secondary",
                                    fontWeight: 600,
                                    letterSpacing: 1,
                                    display: "block",
                                    mb: 2,
                                }}
                            >
                                Company Info
                            </Typography>
                            <Stack spacing={2}>
                                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                                    <TextField
                                        fullWidth
                                        required
                                        label="Workspace Name"
                                        placeholder="e.g. TechCorp"
                                        value={form.workspaceName}
                                        onChange={(e) => setFormField("workspaceName", e.target.value)}
                                        size="small"
                                    />
                                    <TextField
                                        fullWidth
                                        required
                                        label="Website Link"
                                        placeholder="https://company.com"
                                        value={form.websiteLink}
                                        onChange={(e) => setFormField("websiteLink", e.target.value)}
                                        size="small"
                                    />
                                </Stack>
                                <TextField
                                    fullWidth
                                    required
                                    label="About the Company"
                                    placeholder="Brief description about the company"
                                    value={form.aboutCompany}
                                    onChange={(e) => setFormField("aboutCompany", e.target.value)}
                                    multiline
                                    rows={3}
                                    size="small"
                                />
                                <Box>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        Upload Logo
                                    </Typography>
                                    <Box
                                        onClick={() => fileInputRef.current?.click()}
                                        sx={{
                                            border: "1px dashed",
                                            borderColor: "divider",
                                            borderRadius: 1,
                                            py: logoPreviewUrl ? 2 : 4,
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            gap: 1,
                                            cursor: "pointer",
                                            bgcolor: "action.hover",
                                            "&:hover": { borderColor: "primary.main", bgcolor: "action.selected" },
                                        }}
                                    >
                                        {logoPreviewUrl ? (
                                            <>
                                                <Box
                                                    component="img"
                                                    src={logoPreviewUrl}
                                                    alt="Logo preview"
                                                    sx={{
                                                        maxWidth: 120,
                                                        maxHeight: 120,
                                                        objectFit: "contain",
                                                        borderRadius: 1,
                                                    }}
                                                />
                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {logoFile?.name}
                                                    </Typography>
                                                    <Button
                                                        size="small"
                                                        color="error"
                                                        variant="soft"
                                                        onClick={handleRemoveLogo}
                                                    >
                                                        Remove
                                                    </Button>
                                                </Stack>
                                            </>
                                        ) : (
                                            <>
                                                <Iconify icon="mdi:upload" width={32} sx={{ color: "text.disabled" }} />
                                                <Typography variant="body2" color="text.secondary">
                                                    Click to upload (PNG, JPEG)
                                                </Typography>
                                            </>
                                        )}
                                    </Box>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/png,image/jpeg,image/jpg"
                                        style={{ display: "none" }}
                                        onChange={handleFileChange}
                                    />
                                </Box>
                            </Stack>
                        </Box>

                        {/* CREDIT TYPES */}
                        <Box>
                            <Typography
                                variant="overline"
                                sx={{
                                    color: "text.secondary",
                                    fontWeight: 600,
                                    letterSpacing: 1,
                                    display: "block",
                                    mb: 2,
                                }}
                            >
                                Credit Types{" "}
                                <Box component="span" sx={{ color: "error.main" }}>
                                    *
                                </Box>
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                                At least one credit type is required
                            </Typography>
                            <Stack spacing={2}>
                                <CreditTypeToggleRow
                                    title="Video"
                                    description="Enable video-based interviews"
                                    checked={form.videoEnabled}
                                    onCheckedChange={(v) => setFormField("videoEnabled", v)}
                                />

                                {form.videoEnabled && (
                                    <CreditTypeConfigSection
                                        type="video"
                                        config={form.videoConfig as CreditTypeConfig}
                                        onChange={(config) =>
                                            setFormField("videoConfig", normalizeCreditConfig(config))
                                        }
                                        errors={
                                            {
                                                credits: fieldErrors["videoConfig.credits"],
                                                pulseRateMinsPerCredit:
                                                    fieldErrors["videoConfig.pulseRateMinsPerCredit"],
                                                maxCreditsPerCall: fieldErrors["videoConfig.maxCreditsPerCall"],
                                                pulse: fieldErrors["videoConfig.pulse"],
                                            } as CreditTypeConfigSectionProps["errors"]
                                        }
                                    />
                                )}

                                <CreditTypeToggleRow
                                    title="Audio"
                                    description="Enable audio-based interviews"
                                    checked={form.audioEnabled}
                                    onCheckedChange={(v) => setFormField("audioEnabled", v)}
                                />

                                {form.audioEnabled && (
                                    <CreditTypeConfigSection
                                        type="audio"
                                        config={form.audioConfig as CreditTypeConfig}
                                        onChange={(config) =>
                                            setFormField("audioConfig", normalizeCreditConfig(config))
                                        }
                                        errors={
                                            {
                                                credits: fieldErrors["audioConfig.credits"],
                                                pulseRateMinsPerCredit:
                                                    fieldErrors["audioConfig.pulseRateMinsPerCredit"],
                                                maxCreditsPerCall: fieldErrors["audioConfig.maxCreditsPerCall"],
                                                pulse: fieldErrors["audioConfig.pulse"],
                                            } as CreditTypeConfigSectionProps["errors"]
                                        }
                                    />
                                )}

                                <CreditTypeToggleRow
                                    title="Outbound Audio"
                                    description="Enable outbound (phone) audio interviews"
                                    checked={outboundAudioEnabled}
                                    onCheckedChange={setOutboundAudioEnabled}
                                />

                                {outboundAudioEnabled && (
                                    <CreditTypeConfigSection
                                        type="outboundAudio"
                                        config={form.outboundAudioConfig as CreditTypeConfig}
                                        onChange={(config) =>
                                            setFormField("outboundAudioConfig", normalizeCreditConfig(config))
                                        }
                                        errors={
                                            {
                                                credits: fieldErrors["outboundAudioConfig.credits"],
                                                pulseRateMinsPerCredit:
                                                    fieldErrors["outboundAudioConfig.pulseRateMinsPerCredit"],
                                                maxCreditsPerCall: fieldErrors["outboundAudioConfig.maxCreditsPerCall"],
                                                pulse: fieldErrors["outboundAudioConfig.pulse"],
                                            } as CreditTypeConfigSectionProps["errors"]
                                        }
                                    />
                                )}

                                {anyCreditTypeEnabled && (
                                    <>
                                        <Divider sx={{ my: 2 }} />
                                        <Box sx={{ pt: 1 }}>
                                            <Typography
                                                variant="overline"
                                                sx={{
                                                    color: "text.secondary",
                                                    fontWeight: 600,
                                                    letterSpacing: 1,
                                                    display: "block",
                                                    mb: 2,
                                                }}
                                            >
                                                ATTEMPTS & CHARGING
                                            </Typography>
                                            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                                                <TextField
                                                    fullWidth
                                                    label="Attempt Frequency"
                                                    type="number"
                                                    value={form.attemptFrequency}
                                                    onChange={(e) => setFormField("attemptFrequency", e.target.value)}
                                                    size="small"
                                                    inputProps={{ min: 0 }}
                                                    error={Boolean(fieldErrors.attemptFrequency)}
                                                    helperText={fieldErrors.attemptFrequency}
                                                />
                                                <TextField
                                                    fullWidth
                                                    select
                                                    label="Charging Type"
                                                    value={form.chargingType}
                                                    onChange={(e) =>
                                                        setFormField(
                                                            "chargingType",
                                                            e.target.value as ChargingTypeValue
                                                        )
                                                    }
                                                    size="small"
                                                    SelectProps={{ displayEmpty: false }}
                                                >
                                                    {CHARGING_TYPE_OPTIONS.map((opt) => (
                                                        <MenuItem key={opt.value} value={opt.value}>
                                                            {opt.label}
                                                        </MenuItem>
                                                    ))}
                                                </TextField>
                                            </Stack>
                                        </Box>
                                    </>
                                )}
                            </Stack>
                        </Box>
                    </Stack>
                </Box>

                <Box
                    sx={{
                        flexShrink: 0,
                        pt: 2,
                        mt: 2,
                    }}
                >
                    <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        fullWidth
                        disabled={submitting || isCreateDisabled}
                    >
                        {submitting ? "Creating..." : "Create Workspace"}
                    </Button>
                </Box>
            </Box>
        </CenteredModal>
    )
}
